<template>
  <b-card class="calibration-diagram mb-3" no-body>
    <div slot="header" class="d-flex justify-content-between align-items-center">
      <span><i class="fa fa-diagram-project mr-1"></i> How the calibration value is applied</span>
      <b-button size="sm" variant="link" class="py-0" @click="expanded = !expanded">
        {{ expanded ? 'Hide' : 'Show' }} diagram
      </b-button>
    </div>

    <div v-if="expanded" class="p-3">
      <b-row>

        <!-- SVG diagram -->
        <b-col md="6" class="text-center">
          <!-- Well cross-section -->
          <svg v-if="isDepthToWater" viewBox="0 0 500 420" xmlns="http://www.w3.org/2000/svg" class="cal-svg">
            <defs>
              <marker id="arrowStart" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                <path d="M 10 0 L 0 5 L 10 10 z" fill="context-stroke" />
              </marker>
              <marker id="arrowEnd" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
              </marker>
            </defs>

            <!-- Ground / hatch above casing top -->
            <pattern id="ground" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke="#a58c66" stroke-width="1"/>
            </pattern>
            <rect x="120" y="10" width="260" height="30" fill="url(#ground)"/>

            <!-- Casing (double walls) -->
            <rect x="200" y="40" width="100" height="360" fill="#eaeaea" stroke="#333" stroke-width="2"/>
            <line x1="210" y1="40" x2="210" y2="400" stroke="#333" stroke-width="1"/>
            <line x1="290" y1="40" x2="290" y2="400" stroke="#333" stroke-width="1"/>

            <!-- Casing top reference line -->
            <line x1="130" y1="40" x2="380" y2="40" stroke="#333" stroke-width="2" stroke-dasharray="4,3"/>
            <text x="125" y="45" text-anchor="end" font-size="12" font-weight="bold" fill="#333">{{ isWell ? 'Casing top' : 'Benchmark' }}</text>
            <text x="125" y="60" text-anchor="end" font-size="10" fill="#666">datum (0 ft)</text>

            <!-- Water in well -->
            <rect x="210" y="220" width="80" height="180" fill="#B8DDF5" opacity="0.75"/>
            <path d="M210,220 Q220,215 230,220 T250,220 T270,220 T290,220" fill="none" stroke="#3d80c2" stroke-width="2"/>

            <!-- Sensor -->
            <rect x="240" y="378" width="20" height="16" rx="2" fill="#3a3a3a" stroke="#000" stroke-width="1"/>
            <circle cx="250" cy="386" r="3" fill="#e5c100"/>
            <text x="315" y="390" font-size="11" fill="#333">Pressure sensor</text>
            <line x1="260" y1="386" x2="310" y2="388" stroke="#666" stroke-width="1"/>

            <!-- DTW arrow (left side) -->
            <line x1="170" y1="40" x2="170" y2="220" stroke="#d9534f" stroke-width="2"
                  marker-start="url(#arrowStart)" marker-end="url(#arrowEnd)"/>
            <text x="163" y="135" text-anchor="end" font-size="12" font-weight="bold" fill="#d9534f">DTW</text>
            <text x="163" y="150" text-anchor="end" font-size="10" fill="#666">= depth_f</text>

            <!-- Water column above sensor arrow (right side, inside) -->
            <line x1="345" y1="220" x2="345" y2="386" stroke="#3d80c2" stroke-width="2"
                  marker-start="url(#arrowStart)" marker-end="url(#arrowEnd)"/>
            <text x="352" y="300" font-size="12" font-weight="bold" fill="#3d80c2">raw</text>
            <text x="352" y="315" font-size="10" fill="#666">water column</text>
            <text x="352" y="328" font-size="10" fill="#666">above sensor</text>

            <!-- Sensor depth arrow (far right) -->
            <line x1="410" y1="40" x2="410" y2="386" stroke="#5cb85c" stroke-width="2"
                  marker-start="url(#arrowStart)" marker-end="url(#arrowEnd)"/>
            <text x="417" y="210" font-size="12" font-weight="bold" fill="#5cb85c">|cal|</text>
            <text x="417" y="225" font-size="10" fill="#666">sensor depth</text>
            <text x="417" y="238" font-size="10" fill="#666">below casing</text>
          </svg>

          <!-- Flume / direct-depth sensor -->
          <svg v-else viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" class="cal-svg">
            <defs>
              <marker id="arrowStart2" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                <path d="M 10 0 L 0 5 L 10 10 z" fill="context-stroke" />
              </marker>
              <marker id="arrowEnd2" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
              </marker>
            </defs>

            <!-- Channel floor — the measurement datum -->
            <rect x="40" y="238" width="420" height="34" fill="#c8b48c" stroke="#8a6d3b" stroke-width="1"/>
            <line x1="20" y1="238" x2="470" y2="238" stroke="#8a6d3b" stroke-width="2"/>
            <text x="40" y="288" font-size="11" fill="#8a6d3b">{{ datumLabel }} — datum (0 ft)</text>

            <!-- Water -->
            <path d="M40,168 Q120,163 200,168 T360,168 T460,168 L460,238 L40,238 Z" fill="#B8DDF5" opacity="0.75"/>
            <path d="M40,168 Q120,163 200,168 T360,168 T460,168" fill="none" stroke="#3d80c2" stroke-width="2"/>

            <!-- Logger enclosure above the water, cable down to the sensor -->
            <line x1="150" y1="60" x2="150" y2="224" stroke="#666" stroke-width="2"/>
            <rect x="120" y="30" width="60" height="30" rx="4" fill="#3a3a3a" stroke="#000"/>
            <text x="150" y="24" text-anchor="middle" font-size="10" fill="#666">Logger</text>

            <!-- Level sensor sitting at the datum, submerged -->
            <rect x="136" y="222" width="28" height="15" rx="2" fill="#3a3a3a" stroke="#000" stroke-width="1"/>
            <circle cx="150" cy="222" r="3" fill="#e5c100"/>
            <line x1="164" y1="229" x2="204" y2="234" stroke="#666" stroke-width="1"/>
            <text x="208" y="232" font-size="11" fill="#333">Level sensor</text>
            <text x="208" y="245" font-size="10" fill="#666">reports depth above datum</text>

            <!-- Reference lines: water surface, and surface + cal -->
            <line x1="320" y1="168" x2="450" y2="168" stroke="#3d80c2" stroke-width="1" stroke-dasharray="3,3" opacity="0.7"/>
            <line x1="386" y1="156" x2="450" y2="156" stroke="#5cb85c" stroke-width="1" stroke-dasharray="3,3" opacity="0.9"/>

            <!-- raw arrow: datum to actual water surface -->
            <line x1="330" y1="238" x2="330" y2="168" stroke="#3d80c2" stroke-width="2"
                  marker-start="url(#arrowStart2)" marker-end="url(#arrowEnd2)"/>
            <text x="338" y="192" font-size="12" font-weight="bold" fill="#3d80c2">raw</text>
            <text x="338" y="206" font-size="10" fill="#666">measured</text>
            <text x="338" y="218" font-size="10" fill="#666">depth</text>

            <!-- cal: the small trim between raw and the published value -->
            <text x="400" y="146" text-anchor="middle" font-size="11" font-weight="bold" fill="#5cb85c">cal</text>
            <line x1="400" y1="168" x2="400" y2="156" stroke="#5cb85c" stroke-width="2"
                  marker-start="url(#arrowStart2)" marker-end="url(#arrowEnd2)"/>

            <!-- depth_f arrow: datum to the published value (raw + cal) -->
            <line x1="430" y1="238" x2="430" y2="156" stroke="#d9534f" stroke-width="2"
                  marker-start="url(#arrowStart2)" marker-end="url(#arrowEnd2)"/>
            <text x="438" y="200" font-size="12" font-weight="bold" fill="#d9534f">depth_f</text>
          </svg>
        </b-col>

        <!-- Explanation panel -->
        <b-col md="6">
          <h6 class="mb-2 text-uppercase small text-muted" style="letter-spacing:0.05em;">Firmware formula</h6>
          <div class="formula-box p-2 mb-3">
            <code>depth_f = |raw + cal|</code>
          </div>

          <template v-if="isDepthToWater">
            <p class="small mb-2">
              For a <strong>{{ sensorType }}</strong>, the pressure transducer sits below the water and produces
              <strong class="text-primary">raw</strong> = feet of water column above the sensor.
              The calibration value encodes <strong>the negative of the sensor's depth below the {{ datumLabel.toLowerCase() }}</strong>,
              so <code>cal = −(sensor depth)</code>.
            </p>
            <p v-if="!isWell" class="small mb-2 text-muted">
              That depth is never taped directly — it is derived as
              <code>D = d + P</code>, where <code>d</code> is the taped distance from the benchmark down to
              the water surface and <code>P</code> is the sensor's own reading. The illustration draws a
              well casing; for a river the same geometry applies to an open channel.
            </p>
            <p class="small mb-2">
              As the water rises, <code>raw</code> increases and <code>|raw + cal|</code> decreases —
              that's depth-to-water shrinking, exactly what you'd expect.
            </p>

            <div class="worked-example small mt-3">
              <div class="text-uppercase small text-muted mb-1" style="letter-spacing:0.05em;">Current state</div>
              <table class="table table-sm mb-0">
                <tbody>
                  <tr>
                    <td class="text-muted">cal (stored on this sensor)</td>
                    <td class="text-right"><code>{{ formatNum(cal) }}</code></td>
                  </tr>
                  <tr>
                    <td class="text-muted">Implied sensor depth <code>|cal|</code></td>
                    <td class="text-right"><code>{{ formatNum(Math.abs(cal)) }} ft</code></td>
                  </tr>
                  <tr v-if="hasWorkedExample">
                    <td class="text-muted">raw (sample: water column)</td>
                    <td class="text-right"><code>{{ formatNum(sampleRaw) }} ft</code></td>
                  </tr>
                  <tr v-if="hasWorkedExample" class="table-active">
                    <td><strong>depth_f = |raw + cal|</strong></td>
                    <td class="text-right"><strong>= {{ formulaWork }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <template v-else>
            <p class="small mb-2">
              For a <strong>{{ sensorType || 'direct-depth' }}</strong> sensor, <strong class="text-primary">raw</strong>
              is already the water depth above the datum — the {{ datumLabel.toLowerCase() }}. <code>cal</code> is a
              small trim offset used to correct minor drift or mounting variation.
            </p>
            <p class="small mb-2">
              A positive <code>cal</code> shifts every reading up; a negative <code>cal</code> shifts every reading down.
              At <code>cal = 0</code> the device reports the raw sensor value.
            </p>
            <p class="small mb-2 text-muted">
              If the hardware is a downward-looking ultrasonic rather than a submerged transducer, the firmware
              converts range-to-water into depth <em>before</em> <code>cal</code> is applied — so <code>raw</code> is
              always a depth here, and <code>cal</code> is never the sensor's mounting height.
            </p>

            <div class="worked-example small mt-3">
              <div class="text-uppercase small text-muted mb-1" style="letter-spacing:0.05em;">Current state</div>
              <table class="table table-sm mb-0">
                <tbody>
                  <tr>
                    <td class="text-muted">cal (stored on this sensor)</td>
                    <td class="text-right"><code>{{ formatNum(cal) }} ft</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </b-col>
      </b-row>

      <p class="text-muted small mb-0 mt-3">
        <i class="fa fa-info-circle mr-1"></i>
        Calibrating means picking a <code>cal</code> such that the firmware's <code>|raw + cal|</code> equals your
        known reference at that moment.
      </p>
    </div>
  </b-card>
</template>

<script>
export default {
  name: 'CalibrationDiagram',
  props: {
    sensorType: {
      type: String,
      default: null
    },
    cal: {
      type: Number,
      default: 0
    },
    // Optional live values for a worked example
    sampleRaw: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      expanded: true
    }
  },
  computed: {
    isWell () {
      return this.sensorType === 'well'
    },
    // A river reuses the well sensor and its calibration convention: cal is the negative
    // of the sensor's depth below a surveyed benchmark, not a near-zero additive trim.
    isDepthToWater () {
      return this.sensorType === 'well' || this.sensorType === 'river'
    },
    // What "0 ft" means for this device — the surface the sensor measures depth above.
    datumLabel () {
      if (this.sensorType === 'well') return 'Casing top'
      if (this.sensorType === 'flume') return 'Flume floor at the measuring point'
      if (this.sensorType === 'plunge') return 'Pool bottom'
      if (this.sensorType === 'river') return 'Surveyed benchmark'
      if (this.sensorType === 'bubbler') return 'Bubbler orifice'
      return 'Channel bottom'
    },
    hasWorkedExample () {
      return this.sampleRaw !== null && !isNaN(this.sampleRaw)
    },
    formulaWork () {
      if (!this.hasWorkedExample) return ''
      const sum = this.sampleRaw + this.cal
      return `|${this.formatNum(this.sampleRaw)} + (${this.formatNum(this.cal)})| = ${this.formatNum(Math.abs(sum))} ft`
    }
  },
  methods: {
    formatNum (n) {
      if (n === null || n === undefined || isNaN(n)) return '—'
      const rounded = Math.round(n * 10000) / 10000
      return String(rounded)
    }
  }
}
</script>

<style scoped>
.cal-svg {
  max-width: 100%;
  height: auto;
  max-height: 380px;
}
.formula-box {
  background: #f7f7f9;
  border: 1px solid #dcdcdc;
  border-radius: 4px;
  text-align: center;
  font-size: 1.05rem;
}
.worked-example {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 6px 8px;
}
.worked-example .table {
  background: transparent;
}
</style>
